import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { ColumnDefinition, ColumnVisibility } from '../../../../core/model/column-definition';
import { RadioGroupItem } from '../../../../core/component/radio-group/radio-group-item';
import { SidebarItem } from '../../../model/selectable-items';
import { FormControlExFactory } from '../../../service/factory/form-control-ex-factory.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LanguageService } from '../../../service/language.service';

@Component({
    selector: 'app-excel-management-sidebar',
    templateUrl: './excel-management-sidebar.component.html',
    styleUrls: ['./excel-management-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExcelManagementSidebarComponent implements OnInit, OnChanges {
  @Input() items: SidebarItem[];
  @Input() open: boolean;
  @Input() columnDefinition: ColumnDefinition[];
  @Input() title: string;
  @Input() searchCaption: string;
  @Input() validate: boolean = false;
  @Input() showRadioCheckbox: boolean;
  @Output() onSave = new EventEmitter<SidebarItem[]>();
  @Output() onCancel = new EventEmitter<void>();

  sidebarItems: SidebarItem[];
  reorderable: boolean = false;
  allSelected = false;
  translationInstants: string[];
  constructor(
    private readonly _languageService: LanguageService,
    protected _fcf: FormControlExFactory, protected fb: FormBuilder) {
    this.form = fb.group({
      radioButtons: _fcf.create('exportExcelRadioButtons', null),
      searchFilter: _fcf.create('SEARCH_COLUMNS', ''),
    });
  }
  form: FormGroup;

  radioItems: {
    radioValue: 'exportListViewColumns' | 'exportPreferencesColumns';
    radioDisplayName: string;
    checkboxValue: boolean;
    checkboxDisplayName: string;
  }[] = [
    {
      radioValue: 'exportListViewColumns',
      radioDisplayName: 'EXCEL_EXPORT_SETTINGS.EXPORT_LIST_VIEW_COLUMNS',
      checkboxValue: true,
      checkboxDisplayName: 'EXCEL_EXPORT_SETTINGS.APPLY_SELECTION',
    },
    {
      radioValue: 'exportPreferencesColumns',
      radioDisplayName: 'EXCEL_EXPORT_SETTINGS.EXPORT_PREFERENCES_COLUMNS',
      checkboxValue: true,
      checkboxDisplayName: 'EXCEL_EXPORT_SETTINGS.APPLY_SELECTION',
    },
  ];

  readonly radioGroupItems: RadioGroupItem<'exportListViewColumns' | 'exportPreferencesColumns'>[] = [
    {
      value: 'exportListViewColumns',
      displayName: 'EXCEL_EXPORT_SETTINGS.EXPORT_LIST_VIEW_COLUMNS',
    },
    {
      value: 'exportPreferencesColumns',
      displayName: 'EXCEL_EXPORT_SETTINGS.EXPORT_PREFERENCES_COLUMNS',
    },
  ];

  onApplySelectionChange(checked: boolean): void {
    const item = this.currentRadioItem;
    if (!item) return;

    item.checkboxValue = checked;
  }

  get isApplySelectionChecked(): boolean {
    return !!this.currentRadioItem?.checkboxValue;
  }

  get isListViewModeSelected(): boolean {
    return this.currentRadioValue === 'exportListViewColumns';
  }

  get currentCheckboxDisplayName(): string {
    return this.currentRadioItem?.checkboxDisplayName ?? 'EXCEL_EXPORT_SETTINGS.APPLY_SELECTION';
  }

  ngOnInit() {
    this.reorderable = true;
    this.ensureCurrentRadioInitialized();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['open']?.currentValue) {
      this.processItems();
      this.initialOrder();
      this.ensureCurrentRadioInitialized();
    }

    if (changes['items'] && !changes['open']?.currentValue && this.open) {
      this.processItems();
      this.initialOrder();
    }
  }

  private get currentRadioValue(): 'exportListViewColumns' | 'exportPreferencesColumns' {
    return (this.form.controls['radioButtons'].value as 'exportListViewColumns' | 'exportPreferencesColumns') ?? 'exportListViewColumns';
  }

  private get currentRadioItem() {
    return this.radioItems.find(i => i.radioValue === this.currentRadioValue);
  }

  private ensureCurrentRadioInitialized(): void {
    const ctrl = this.form.controls['radioButtons'];
    const ctrlValue = ctrl.value as 'exportListViewColumns' | 'exportPreferencesColumns' | null;
    if (ctrlValue === null) {
      ctrl.setValue('exportListViewColumns');
    }
  }

  private initialOrder(): void {
    if (this.reorderable) this.orderSidebarSections();
  }

  sidebarSave(): void {
    this.onSave.emit(this.sidebarItems);
    this.sidebarCancel();
  }

  sidebarCancel(): void {
    this.onCancel.emit();
    this.form.controls['searchFilter'].setValue('');
  }

  restoreDefaults(): void {
    const items = this.sidebarItems;
    if (!items) return;

    const defaultColumns = this.getDefaultColumns();
    const findIndex = (items: SidebarItem[], binding: string) => items.findIndex(x => x.binding === binding);

    if (defaultColumns.length > items.length) {
      throw new Error('Default item count exceeds the numer of defined items');
    }

    defaultColumns.forEach((key, correctIndex) => {
      const itemIndex = findIndex(items, key);
      if (itemIndex === -1) {
        console.warn(`Default item ${key} not found - skipping!`);
      } else {
        const temp = items[correctIndex];
        items[correctIndex] = items[itemIndex];
        items[itemIndex] = temp;
      }
    });

    items.forEach((item, i) => (item.selected = i < defaultColumns.length));
    return;
  }

  private getDefaultColumns(): string[] {
    return this.columnDefinition.filter(x => x.visibility !== ColumnVisibility.optional).map(x => x.binding);
  }

  get selectedItems(): SidebarItem[] {
    return this.sidebarItems?.filter(item => item.selected) || [];
  }

  get deselectedItems(): SidebarItem[] {
    if (!this.sidebarItems) return [];

    const deselected = this.sidebarItems?.filter(item => !item.selected);
    if (this.reorderable) this.alphabeticalSort(deselected);

    return deselected;
  }

  toggleAll(isChecked: boolean) {
    this.sidebarItems.forEach(item => (item.selected = isChecked));
  }

  checkAllSelected(): boolean {
    this.allSelected = this.sidebarItems.every(item => item.selected);
    return this.allSelected;
  }

  setItemSelected(item: SidebarItem, selected: boolean): void {
    item.selected = selected;
    if (this.reorderable) {
      this.orderSidebarSections();
    }
  }

  isAnyCheckboxSelected(): boolean {
    return this.sidebarItems.some(item => item.selected);
  }

  orderSidebarSections(): void {
    const selected = this.selectedItems;
    const deselected = this.deselectedItems;
    this.sidebarItems = [...selected, ...deselected];
  }

  drop(event: CdkDragDrop<SidebarItem[]>) {
    const selectedColumns = this.selectedItems;
    const previousIndex = selectedColumns.findIndex(item => item === this.sidebarItems[event.previousIndex]);
    const currentIndex = selectedColumns.findIndex(item => item === this.sidebarItems[event.currentIndex]);

    moveItemInArray(selectedColumns, previousIndex, currentIndex);
    this.sidebarItems = [...selectedColumns, ...this.deselectedItems];
  }

  private alphabeticalSort(items: SidebarItem[]) {
    return items.sort((a, b) => {
      const captionA = this._languageService.instant(a.caption) as string;
      const captionB = this._languageService.instant(b.caption) as string;
      return captionA.localeCompare(captionB, this._languageService.currentLanguage);
    });
  }

  private processItems() {
    if (!this.items) {
      return;
    }

    this.sidebarItems = this.items.map(item => ({
      selected: item.selected,
      caption: item.caption,
      id: item.id,
      binding: item.binding,
    }));

    this.translationInstants = [];
    this.sidebarItems.forEach(x => this.translationInstants.push(x.caption));
    this.setupTranslatedCaptions();
    if (!this.reorderable) this.alphabeticalSort(this.sidebarItems);
  }

  private setupTranslatedCaptions() {
    if (!this.sidebarItems) return;

    for (let i = 0; i < this.sidebarItems.length; ++i) {
      const item = this.sidebarItems[i];
      const caption = this._languageService.instant(this.translationInstants[i]);
      if (item.id || item.name) item.caption = caption;
    }
  }
}
